<template>
  <div
    class="conversation-details-wrap"
    :class="{ 'with-border-right': !isOnExpandedLayout }"
  >
    <conversation-header
      v-if="showConversationHeader"
      :chat="currentChat"
      :is-contact-panel-open="isContactPanelVisible"
      :show-back-button="isOnExpandedLayout"
      :board-selected="boardSelected"
      :auto-scroll-enabled="autoScrollEnabled"
      :is-lia-sidebar-open="isLiaSidebarVisible"
      @toggle-auto-scroll="onToggleAutoScroll"
      @contact-panel-toggle="onToggleContactPanel"
      @toggle-opportunity-sidebar="handleOpportunitySidebarToggle"
      @toggle-lia-sidebar="handleLiaSidebarToggle"
    />

    <woot-tabs
      v-if="dashboardAppsFiltered.length && currentChat.id"
      :index="activeIndex"
      class="dashboard-app--tabs"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :name="tab.name"
        :show-badge="false"
      />
    </woot-tabs>

    <keep-alive>
      <div v-show="!activeIndex" class="messages-and-sidebar">
        <messages-view
          v-if="currentChat.id"
          :inbox-id="inboxId"
          :is-contact-panel-open="isContactPanelVisible"
          :auto-scroll="autoScrollEnabled"
          @contact-panel-toggle="onToggleContactPanel"
        />

        <operational-dashboard v-else-if="isOperationalDashboardVisible" />

        <empty-state v-else :is-on-expanded-layout="isOnExpandedLayout" />

        <div v-show="showContactPanel" class="conversation-sidebar-wrap">
          <contact-panel
            v-if="showContactPanel"
            :conversation-id="currentChat.id"
            :inbox-id="currentChat.inbox_id"
            :on-toggle="onToggleContactPanel"
            @toggle-opportunity-sidebar="handleOpportunitySidebarToggle"
            @toggle-lia-sidebar="handleLiaSidebarToggle"
          />
        </div>

        <opportunity-sidebar-details
          v-if="isOpportunitySidebarVisible"
          :opportunity="opportunity"
          @toggle-opportunity-sidebar="handleOpportunitySidebarToggle"
        />

        <lia-sidebar
          v-if="isLiaSidebarVisible"
          @toggle-lia-sidebar="handleLiaSidebarToggle"
        />
      </div>
    </keep-alive>

    <dashboard-app-frame
      v-for="(dashboardApp, index) in dashboardAppsFiltered"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardAppsFiltered[index].content"
      :current-chat="currentChat"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel';
import OpportunitySidebarDetails from 'dashboard/routes/dashboard/conversation/OpportunitySidebarDetails';
import LiaSidebar from 'dashboard/routes/dashboard/conversation/LiaSidebar';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import liaCopilotMixin from 'dashboard/mixins/liaCopilotMixin';
import ConversationHeader from './ConversationHeader';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState';
import MessagesView from './MessagesView';
import OperationalDashboard from './OperationalDashboard/Index.vue';

export default {
  components: {
    ContactPanel,
    OpportunitySidebarDetails,
    LiaSidebar,
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    OperationalDashboard,
  },
  mixins: [liaCopilotMixin],
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
    boardSelected: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      activeIndex: 0,
      autoScrollEnabled: true,
      isOpportunitySidebarOpen: false,
      isLiaSidebarOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
      isEmbedding: 'isEmbedding',
      opportunity: 'opportunities/getOpportunity',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isOperationalDashboardVisible() {
      if (this.currentRole === 'embed' || this.isEmbedding) {
        return false;
      }
      return this.$route.name === 'home';
    },
    dashboardAppsFiltered() {
      const { account_id, display_id } = this.currentChat;

      return this.dashboardApps
        .filter(item => item.content[0]?.show_conversation)
        .map(item => {
          item.content.forEach(element => {
            if (element.url) {
              let url = new URL(element.url);
              url.searchParams.set('account_id', account_id);
              url.searchParams.set('conversation_id', display_id);
              element.url = url.toString();
            }
          });
          return item;
        });
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardAppsFiltered.map(dashboardApp => ({
          key: `dashboard-${dashboardApp.id}`,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      if (this.currentRole === 'embed' || this.isEmbedding) {
        return false;
      }
      return this.isContactPanelVisible && this.currentChat.id;
    },
    isOpportunitySidebarVisible() {
      return (
        this.isOpportunitySidebarOpen &&
        !!this.currentChat.id &&
        !!this.opportunity
      );
    },
    isLiaSidebarVisible() {
      return (
        this.isLiaSidebarOpen &&
        !!this.currentChat.id &&
        this.hasValidCopilotIntegration
      );
    },
    isContactPanelVisible() {
      return (
        this.isContactPanelOpen &&
        !this.isOpportunitySidebarVisible &&
        !this.isLiaSidebarVisible
      );
    },
    showConversationHeader() {
      if (this.currentRole === 'embed' || this.isEmbedding) {
        return false;
      }
      return !!this.currentChat.id;
    },
    isOpportunityPipelinesEnabled() {
      return this.isFeatureEnabledonAccount(
        this.currentChat.account_id,
        FEATURE_FLAGS.OPPORTUNITY_PIPELINES
      );
    },
  },
  watch: {
    'currentChat.inbox_id'(inboxId) {
      if (inboxId) {
        this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
      }
    },
    isContactPanelOpen(newVal) {
      if (newVal === true) {
        this.isOpportunitySidebarOpen = false;
        this.isLiaSidebarOpen = false;
      }
    },
    opportunity(newOpportunity) {
      if (!newOpportunity) {
        this.isOpportunitySidebarOpen = false;
      }
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.getScheduledMessage();
      this.fetchOpportunityState();
      this.activeIndex = 0;
      this.isLiaSidebarOpen = false;
    },
  },
  mounted() {
    this.fetchLabels();
    if (this.currentChat.id) {
      this.fetchOpportunityState();
    }
    this.$store.dispatch('dashboardApps/get');
    this.fetchIntegrations();
    window.bus.$on('toggle-auto-scroll', this.onToggleAutoScroll);
    window.bus.$on('opportunity_created', this.onOpportunityCreated);
  },
  beforeDestroy() {
    this.$off('contact-panel-toggle', this.onToggleContactPanel);
    this.$off('change', this.onDashboardAppTabChange);
    window.bus.$off('toggle-auto-scroll', this.onToggleAutoScroll);
    window.bus.$off('opportunity_created', this.onOpportunityCreated);
  },
  methods: {
    fetchIntegrations() {
      if (!this.appIntegrations.length) {
        this.$store.dispatch('integrations/get');
      }
    },
    fetchLabels() {
      if (!this.currentChat.id) return;
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onToggleContactPanel() {
      if (this.isOpportunitySidebarVisible || this.isLiaSidebarVisible) {
        this.isOpportunitySidebarOpen = false;
        this.isLiaSidebarOpen = false;
        if (this.isContactPanelOpen) return;
      }
      this.$emit('contact-panel-toggle');
    },
    handleOpportunitySidebarToggle() {
      this.isOpportunitySidebarOpen = !this.isOpportunitySidebarOpen;
      if (this.isOpportunitySidebarOpen) {
        this.isLiaSidebarOpen = false;
      }
    },
    handleLiaSidebarToggle() {
      this.isLiaSidebarOpen = !this.isLiaSidebarOpen;
      if (this.isLiaSidebarOpen) {
        this.isOpportunitySidebarOpen = false;
      }
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    onToggleAutoScroll(newValue) {
      this.autoScrollEnabled = newValue;
    },
    onOpportunityCreated(data = {}) {
      const conversationId = this.currentChat.primary_key;
      if (!conversationId || Number(data.conversationId) !== conversationId) {
        return;
      }
      this.fetchOpportunityState();
    },
    async getScheduledMessage() {
      await this.$store.dispatch('getScheduledMessage', this.currentChat.id);
    },
    fetchOpportunityState() {
      if (!this.currentChat.primary_key) {
        return;
      }

      // Without the flag both endpoints are denied by their Pundit policies,
      // so requesting them only produces noise - the sidebar stays hidden
      // either way.
      if (!this.isOpportunityPipelinesEnabled) {
        return;
      }

      this.$store.dispatch('opportunityPipelines/fetch');
      this.$store.dispatch('opportunities/fetchByConversation', {
        conversationId: this.currentChat.primary_key,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.conversation-details-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  background: var(--color-background-light);

  &.with-border-right {
    border-right: 1px solid var(--color-border);
  }
}

.dashboard-app--tabs {
  background: var(--white);
  margin-top: -1px;
  min-height: var(--dashboard-app-tabs-height);
}

.messages-and-sidebar {
  display: flex;
  background: var(--b-50);
  margin: 0;
  height: 100%;
  min-height: 0;
}

.conversation-sidebar-wrap {
  border-right: 1px solid var(--color-border);
  height: auto;
  flex: 0 0;
  z-index: var(--z-index-low);
  overflow: auto;
  background: white;
  flex-basis: 100%;

  @include breakpoint(medium up) {
    flex-basis: 28rem;
  }

  @include breakpoint(large up) {
    flex-basis: 30em;
  }

  @include breakpoint(xlarge up) {
    flex-basis: 31em;
  }

  @include breakpoint(xxlarge up) {
    flex-basis: 33rem;
  }

  @include breakpoint(xxxlarge up) {
    flex-basis: 40rem;
  }

  &::v-deep .contact--panel {
    width: 100%;
    height: 100%;
    max-width: 100%;
  }
}
</style>
